/* eslint-disable no-nested-ternary */
import {
  Form,
  AutoComplete,
  BlueBirdTheme,
  DatePicker,
  Dropdown,
  Radio,
  RadioGroup,
  Typography,
} from "@kitbird/core";
import { Controller } from "react-hook-form";
import { ArrowSwitchVerticalIcon } from "../../../../../assets/icons";
import { parseUserInfoString } from "../../utils";
import { useUserPosition } from "../../Provider";
import MultiLineChip from "./MultiLineChip";
import TableCurrentPositions from "./TableCurrentPositions";
import {
  FormGrid,
  ReplacementSection,
  StyledChipWrapper,
  SwitchIconContainer,
} from "./MutationUser.styled";

const MutationForm = () => {
  const {
    user,
    sourceUserDepartmentOptions,
    sourceUserPoolOptions,
    incomingUserOptions,
    handleSelectDepartment,
    mutationForm,
    mutationType,
    replacementUser,
    handleSelectPool,
    setReplacementUserSearch,
    incomingUserPositions,
    hasNextPageIncomingUserOptions,
    fetchNextPageIncomingUserOptions,
    isLoadingIncomingUserDetail,
    replacementUserInput,
    setReplacementUserInput,
    isFetchingIncomingUserOptions,
    isIdleIncomingUserOptions,
  } = useUserPosition();
  const { control, trigger } = mutationForm;

  return (
    <form id="mutation-form" noValidate>
      <FormGrid>
        <div style={{ display: "flex", flexDirection: "column", gap: ".2rem" }}>
          <Typography
            level={6}
            color={BlueBirdTheme.colors.gray.disable}
          >
            Nama
          </Typography>
          <Typography
            level={5}
            color={BlueBirdTheme.colors.gray.subtitle}
            fontWeight="medium"
          >
            {user?.employee_name || "Loading..."}
          </Typography>
        </div>
        <Controller
          control={control}
          name="type_mutation"
          rules={{ required: "Tipe mutasi wajib diisi" }}
          render={({ field: { onChange, value } }) => (
            <RadioGroup
              required
              title="Tipe mutasi"
              onChange={onChange}
              value={value}
            >
              <Radio label="Pool" value="pool" />
              <Radio label="Departemen" value="department" />
            </RadioGroup>
          )}
        />
        {mutationType === "department" && (
          <Controller
            control={control}
            name="departments"
            rules={{
              validate: (value) => value.some((v) => v.checked) || "Departemen wajib diisi",
            }}
            render={({ field, fieldState: { error } }) => {
              const checkedDepartments = field.value.filter(
                (opt) => opt.checked,
              );
              const dropdownValue = checkedDepartments.map(
                (opt) => opt.value as string,
              );
              return (
                <Dropdown
                  {...field}
                  value={dropdownValue}
                  id="department-dropdown"
                  onSelected={handleSelectDepartment}
                  options={field.value || sourceUserDepartmentOptions || []}
                  error={error?.message ? { message: error.message } : undefined}
                  label={checkedDepartments.length > 0 ? "Departemen" : "Pilih departemen"}
                  allowSearch
                  type="chip"
                  multi
                  required
                  placeholder="Cari disini..."
                  valueNode={
                    checkedDepartments.length > 0
                    && checkedDepartments.map((item) => (
                      <MultiLineChip
                        style={{ margin: "5px 5px 0 0" }}
                        key={item.value}
                        label={`${item.label}\n Pool: Warung buncit`}
                        clickable
                        onClickDelete={(e) => {
                          e.stopPropagation();
                          handleSelectDepartment(item);
                        }}
                      />
                    ))
                  }
                />
              );
            }}
          />
        )}
        {mutationType === "pool" && (
          <Controller
            control={control}
            name="pools"
            rules={{
              validate: (value) => value.some((v) => v.checked) || "Pool wajib diisi",
            }}
            render={({ field, fieldState: { error } }) => {
              const checkedPools = field.value.filter(
                (opt) => opt.checked,
              );
              const dropdownValue = checkedPools.map(
                (opt) => opt.value as string,
              );
              return (
                <Dropdown
                  {...field}
                  value={dropdownValue}
                  id="pool-dropdown"
                  onSelected={handleSelectPool}
                  options={field.value || sourceUserPoolOptions}
                  error={error?.message ? { message: error.message } : undefined}
                  label="Pilih pool"
                  allowSearch
                  type="chip"
                  multi
                  required
                  placeholder="Cari disini..."
                  valueNode={
                    checkedPools.length > 0
                    && checkedPools.map((item) => (
                      <StyledChipWrapper
                        style={{ margin: "5px 5px 0 0" }}
                        key={item.value}
                        label={item.label}
                        clickable
                        onClickDelete={(e) => {
                          e.stopPropagation();
                          handleSelectPool(item);
                        }}
                      />
                    ))
                  }
                />
              );
            }}
          />
        )}
        <SwitchIconContainer>
          <ArrowSwitchVerticalIcon />
        </SwitchIconContainer>
        <ReplacementSection>
          <Typography
            level={5}
            color={BlueBirdTheme.colors.gray.subtitle}
            fontWeight="medium"
          >
            Pengganti
          </Typography>
          <Controller
            name="replacement_user"
            control={control}
            rules={{
              required: "Pengganti wajib diisi",
            }}
            render={({ field, fieldState: { error } }) => {
              const updateReplacementUser = (newValue: string) => {
                setReplacementUserInput(newValue);
                setReplacementUserSearch(newValue);
              };
              const resetField = () => {
                field.onChange(null);
                updateReplacementUser("");
              };
              const getHelperText = () => {
                if (replacementUserInput.length < 3) { return ""; }
                if (isFetchingIncomingUserOptions) { return "Loading..."; }
                if (!isIdleIncomingUserOptions && !incomingUserOptions.length) {
                  return "Maaf, Data yang anda cari belum tersedia";
                }
                return "";
              };
              return (
                <AutoComplete
                  ref={field.ref}
                  name={field.name}
                  onBlur={field.onBlur}
                  dataTestId="replacement-user-input"
                  id="replacement-user-autocomplete"
                  label="Nama yang menggantikan"
                  options={incomingUserOptions}
                  value={replacementUserInput}
                  onSelected={(val) => {
                    field.onChange(val);
                    updateReplacementUser(val.label);
                  }}
                  onClear={resetField}
                  onInputChange={(val) => {
                    if (parseUserInfoString(replacementUserInput)) resetField();
                    else updateReplacementUser(val);
                  }}
                  error={error?.message ? { message: error.message } : undefined}
                  hasNextPage={hasNextPageIncomingUserOptions}
                  fetchNextPage={fetchNextPageIncomingUserOptions}
                  required
                  notFoundContent={(
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Typography level={7}>{getHelperText()}</Typography>
                    </div>
                  )}
                />
              );
            }}
          />
          {replacementUser && (
            <TableCurrentPositions
              dataSource={[...incomingUserPositions]}
              isLoading={isLoadingIncomingUserDetail}
            />
          )}
          <Controller
            name="date"
            control={control}
            rules={{ required: "Tanggal wajib diisi" }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                startDate={field.value}
                variant="default"
                label="Pilih tanggal SK"
                onChange={(date) => {
                  field.onChange(date);
                  trigger("date");
                }}
                onSubmit={() => trigger("date")}
                onReset={() => field.onChange("")}
                error={error?.message ? { message: error.message } : undefined}
                dateFormat="dd MMMM yyyy"
                required
                maxDate={new Date()}
              />
            )}
          />
          <Controller
            name="reason"
            control={control}
            rules={{ required: "Alasan wajib diisi" }}
            render={({ field, fieldState }) => (
              <Form
                {...field}
                id={field.name}
                type="text"
                label="Alasan"
                error={
                  fieldState.error?.message
                    ? { message: fieldState.error.message }
                    : undefined
                }
                required
              />
            )}
          />
        </ReplacementSection>
      </FormGrid>
    </form>
  );
};

export default MutationForm;
